import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Radio, Users, Sparkles, X } from "lucide-react";
import LivePreflight from "@/components/LivePreflight";

/**
 * MatrixShopping live-stream discovery page. Renders the currently-live rooms
 * as neon cards + a large "Go Live" CTA for the current user. Uses the
 * existing polling pattern rather than websockets — the grid refreshes
 * every 15s and any user opening the page while their friend is going
 * live sees the room within one poll cycle.
 */
export default function LiveGrid() {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [title, setTitle] = useState("");
  const [allowBattle, setAllowBattle] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    async function pull() {
      try {
        const { data } = await api.get("/live/discover");
        if (alive) setRooms(data.rooms || []);
      } catch { /* silent */ }
      if (alive) setLoading(false);
    }
    pull();
    const id = setInterval(pull, 15000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  async function handleGoLive() {
    if (!user) { navigate("/login"); return; }
    setStarting(true);
    try {
      const { data } = await api.post("/live/start", {
        title: title.trim().slice(0, 120),
        allow_battle: allowBattle,
      });
      navigate(`/live/${data.room_name}?host=1`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't start stream";
      const { toast } = await import("sonner");
      toast.error(msg);
    } finally {
      setStarting(false);
    }
  }

  async function endRoom(roomName, e) {
    e.stopPropagation();
    e.preventDefault();
    const { toast } = await import("sonner");
    if (!window.confirm("End this stream? It'll disappear from the grid.")) return;
    try {
      await api.post(`/live/${roomName}/end`);
      // Optimistically drop it from the grid
      setRooms((prev) => prev.filter((r) => r.room_name !== roomName));
      toast.success("Stream ended");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't end stream");
    }
  }

  return (
    <div className="min-h-screen bg-[#050505] pb-24 px-4 pt-6 md:px-8">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <span className="h-2 w-2 rounded-full bg-[#FF007F] animate-pulse" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF007F]">
              // signal.live
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-white">
            GO <span className="text-[#39FF14]">LIVE</span>
          </h1>
          <p className="text-white/60 text-sm mt-2 font-mono">
            Stream to your fans in real time. Chat, gifts, and battles — all in one room.
          </p>
        </div>

        {/* Go-Live composer */}
        <div className="bg-[#0A0A0C] border border-[#FF007F]/30 p-6 mb-10 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none opacity-30 bg-gradient-to-br from-[#FF007F]/10 via-transparent to-[#39FF14]/10" />
          <div className="relative grid gap-6 md:grid-cols-[minmax(0,1fr)_260px] items-start">
            {/* Preflight preview */}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2 font-mono">
                // camera.mic.check
              </div>
              <LivePreflight
                onStatusChange={({ ready }) => setPreviewReady(ready)}
              />
              <p className="text-[10px] text-white/40 mt-2 font-mono">
                See yourself + green mic bars? You&apos;re ready. If it stays black, tap the retry button.
              </p>
            </div>

            {/* Title + battle + CTA */}
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-white/50 mb-2 font-mono">
                Stream title
              </label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Late night vibes with @you"
                maxLength={120}
                data-testid="live-title-input"
                className="w-full bg-black/60 border border-white/10 focus:border-[#39FF14] px-3 py-2 text-sm text-white outline-none font-mono"
              />
              <label className="flex items-center gap-2 mt-4 cursor-pointer text-xs text-white/70">
                <input
                  type="checkbox"
                  checked={allowBattle}
                  onChange={(e) => setAllowBattle(e.target.checked)}
                  data-testid="live-allow-battle"
                  className="accent-[#FF007F]"
                />
                <Sparkles className="w-3.5 h-3.5 text-[#FF007F]" />
                Allow BATTLE mode
              </label>
              <button
                onClick={handleGoLive}
                disabled={starting || !previewReady}
                data-testid="live-go-btn"
                className="mt-5 w-full px-6 py-3 bg-[#FF007F] hover:bg-[#FF3399] text-white font-black uppercase tracking-widest text-sm disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {starting
                  ? "Connecting…"
                  : previewReady
                    ? "▶ GO LIVE NOW"
                    : "// waiting.for.camera"}
              </button>
              <p className="text-[10px] text-white/40 mt-3 font-mono">
                Post at least one video before you can go live.
              </p>
            </div>
          </div>
        </div>

        {/* Live grid */}
        <div className="flex items-center gap-2 mb-4">
          <Radio className="w-4 h-4 text-[#FF007F]" />
          <span className="text-xs font-mono uppercase tracking-widest text-white/70">
            Live now — {rooms.length}
          </span>
        </div>
        {loading ? (
          <p className="text-white/40 text-sm font-mono">// scanning.rooms…</p>
        ) : rooms.length === 0 ? (
          <div className="border border-dashed border-white/10 p-10 text-center">
            <p className="text-white/50 text-sm">No one is live right now. Be the first!</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {rooms.map((r) => {
              const isMine = user && r.broadcaster_id === user.user_id;
              return (
              <div key={r.room_name} className="relative group">
                <button
                  onClick={() => navigate(`/live/${r.room_name}${isMine ? "?host=1" : ""}`)}
                  data-testid={`live-card-${r.room_name}`}
                  className="w-full text-left bg-[#0A0A0C] border border-white/10 hover:border-[#FF007F] transition-colors relative overflow-hidden"
                >
                  <div className="aspect-[3/4] bg-gradient-to-br from-[#FF007F]/20 via-[#050505] to-[#39FF14]/10 relative flex items-center justify-center">
                    {r.broadcaster_picture ? (
                      <img
                        src={r.broadcaster_picture}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover opacity-60"
                      />
                    ) : null}
                    <span className="absolute top-2 left-2 bg-[#FF007F] text-white text-[10px] font-black px-2 py-0.5 flex items-center gap-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
                      LIVE
                    </span>
                    <span className="absolute top-2 right-2 bg-black/60 text-white text-[10px] px-2 py-0.5 flex items-center gap-1 font-mono">
                      <Users className="w-3 h-3" />
                      {r.viewer_count || 0}
                    </span>
                    {r.allow_battle && (
                      <span className="absolute bottom-2 right-2 bg-[#39FF14] text-black text-[10px] font-black px-2 py-0.5">
                        BATTLE
                      </span>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-xs text-white font-bold truncate">
                      {r.title || "Live stream"}
                    </p>
                    <p className="text-[10px] text-white/50 mt-1 truncate">
                      @{r.broadcaster_name || "creator"}
                    </p>
                  </div>
                </button>
                {isMine && (
                  <button
                    onClick={(e) => endRoom(r.room_name, e)}
                    data-testid={`live-end-card-${r.room_name}`}
                    className="absolute bottom-3 left-3 bg-black/70 backdrop-blur-sm border border-[#FF007F] text-[#FF007F] hover:bg-[#FF007F] hover:text-white transition-colors px-2 py-1 flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest z-10"
                    aria-label="End this stream"
                    title="Force-end this stream"
                  >
                    <X className="w-3 h-3" />
                    end
                  </button>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
